import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
} from "@angular/core";
import { Observable, combineLatest, Subject } from "rxjs";
import { Select, Store } from "@ngxs/store";
import {
  ToggleTab,
  GetProductDetails,
  OnChangeVariantForm,
  ClearProductDetailsState,
  GetAccess,
  SelectProductVariant,
  GetRelatedProducts,
  UpdateProductDetails,
  GetProductRelatedBundles,
  GetIncludedProducts,
  SetIncludedProductsPagination,
  OnChangeBundleVariantForm,
  SetProductRelatedBundlesPagination,
} from "../../actions/product-details.actions";
import { ActivatedRoute, Router } from "@angular/router";
import { Product } from "../../models/product.model";
import { ProductVariantShort } from "src/app/common/common-product/models/product-short.model";
import { map, filter, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { IVariantForm } from "../../state/product-details.state";
import { ProductVariantDeliverableModel } from "../../models/product-variant-deliverable.model";
import { Profile } from "../../../common/common-profile/models/profile.model";
import { Contract } from "src/app/snatch/models/contract.model";
import { BREADCRUMBS_CONFIGURATION } from "src/app/core/configurations/breadcrumbs.config";
import { ProductDetailsService } from "../../services/product-details.service";
import { differenceInDays } from "date-fns";
import { AuthTokenState } from "../../../auth/state/auth-token.state";
import { AddLineItemToShoppingCart } from "../../../shopping-cart/actions/shopping-cart.actions";
import { ProductVideo } from "../../models/product-media.model";
import { BreadcrumbsBuildingService } from "src/app/core/services/breadcrumbs-building.service";
import { IBreadcrumb } from "src/app/core/models/breadcrumb.interface";
import { Configuration } from "../../../snatch/models/configuration.model";
import {
  BundleModel,
  CommonPricingQueryModel,
} from "../../models/bundle.model";
import { IPageable } from "../../../snatch/models/page.model";

@Component({
  selector: "leap-product-details",
  templateUrl: "./product-details.component.html",
  styleUrls: ["./product-details.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductDetailsComponent implements OnInit, OnDestroy {
  @Select((state: any) => state.core.profile)
  profile$: Observable<Profile>;

  @Select((state: any) => state.session.userDetails)
  userDetails$: Observable<any>;

  @Select((state: any) => state.productDetails.details)
  product$: Observable<Product>;

  @Select((state: any) => state.productDetails.selectedVariant)
  selectedVariant$: Observable<ProductVariantShort>;

  @Select((state) => state.productDetails.relatedBundles)
  relatedBundles$: Observable<BundleModel[]>;

  @Select((state) => state.productDetails.relatedBundlesPagination)
  relatedBundlesPagination$: Observable<IPageable>;

  @Select((state) => state.productDetails.relatedBundlesTotalCount)
  relatedBundlesTotalCount$: Observable<number>;

  @Select((state) => state.productDetails.includedProducts)
  includedProducts$: Observable<Product[]>;

  @Select((state) => state.productDetails.includedProductsPagination)
  includedProductsPagination$: Observable<IPageable>;

  @Select((state) => state.productDetails.includedProductsTotalCount)
  includedProductsTotalCount$: Observable<number>;

  @Select((state: any) => state.productDetails.productVariantDeliverables)
  productVariantDeliverables$: Observable<
    Map<string, Array<ProductVariantDeliverableModel>>
  >;

  @Select(
    (state: any) => state.productDetails.productVariantDeliverablesPending
  )
  productVariantDeliverablesPending$: Observable<Boolean>;

  @Select((state: any) => state.productDetails.languages)
  languages$: Observable<Configuration[]>;

  @Select((state: any) => state.productDetails.deliveryFormats)
  deliveryFormats$: Observable<Configuration[]>;

  @Select((state: any) => state.productDetails.certificateTypes)
  certificateTypes$: Observable<Configuration[]>;

  @Select((state: any) => state.productDetails.courseDeliveryFormats)
  courseDeliveryFormats$: Observable<string[]>;

  @Select((state: any) => state.productDetails.variantForm)
  variantForm$: Observable<{ model: IVariantForm }>;

  @Select((state: any) => state.productDetails.licenseForm)
  licenseForm$: Observable<{ model: CommonPricingQueryModel }>;

  @Select((state: any) => state.productDetails.tab)
  selectedTab$: Observable<string>;

  @Select((state: any) => state.productDetails.expiringContract)
  expiringContract$: Observable<Contract>;

  @Select((state: any) => state.productDetails.getProductDetailsPending)
  getProductDetailsPending$: Observable<boolean>;

  @Select((state: any) => state.productDetails.getProductDetailsSuccess)
  getProductDetailsSuccess$: Observable<boolean>;

  @Select((state: any) => state.productDetails.segmentsMapper)
  segmentsMapper$: Observable<Map<string, Contract>>;

  @Select((state: any) => state.productDetails.requestEvaluationCopyPending)
  requestEvaluationCopyPending$: Observable<boolean>;

  @Select((state: any) => state.productDetails.relatedProducts)
  relatedProducts$: Observable<Array<any>>;

  //******************************************
  // Dictionaries for fetching the config values
  //******************************************

  @Select((state: any) => state.core.dictionaries.currencySignsMap)
  currencySignsMap$: Observable<Map<string, string>>;

  @Select((state: any) => state.core.dictionaries.dictionaryFetchingPending)
  dictionaryFetchingPending$: Observable<boolean>;

  @Select((state: any) => state.shoppingCart.addLineItemSku)
  addLineItemSku$: Observable<string>;
  @Select((state: any) => state.shoppingCart.addLineItemPending)
  addLineItemPending$: Observable<boolean>;

  @Select(AuthTokenState.isLogged)
  isLogged$: Observable<boolean>;

  breadcrumbs: IBreadcrumb[] = this.breadcrumbsBuildingService.buildBreadcrumbsWithQueryParams(
    [
      BREADCRUMBS_CONFIGURATION.Products,
      BREADCRUMBS_CONFIGURATION.ProductDetails,
    ]
  );

  tabs = [];
  ngUnsubscribe = new Subject();
  videoAboutProduct: ProductVideo;
  isProductBundle: boolean;

  constructor(
    private productDetailsService: ProductDetailsService,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private breadcrumbsBuildingService: BreadcrumbsBuildingService
  ) {}

  ngOnInit() {
    this.subscribeToProductDetails();

    this.route.paramMap.pipe(map((_) => _.get("sku"))).subscribe((sku) => {
      this.store.dispatch([
        new ClearProductDetailsState(),
        new GetProductDetails(sku),
      ]);
    });

    this.route.queryParamMap.subscribe((param) => {
      this.store.dispatch(
        new SelectProductVariant(
          param.get("language"),
          param.get("deliveryFormat"),
          param.get("certificateType"),
          param.get("license")
        )
      );
    });

    const model = this.variantForm$.pipe(
      map((_) => _.model),
      filter((_) => !!_)
    );

    const languageObservable$ = model.pipe(
      map((_) => _.language),
      distinctUntilChanged()
    );
    const deliveryFormatObservable$ = model.pipe(
      map((_) => _.deliveryFormat),
      distinctUntilChanged()
    );
    const certificateTypeObservable$ = model.pipe(
      map((_) => _.certificateType),
      distinctUntilChanged()
    );
    combineLatest(
      languageObservable$,
      deliveryFormatObservable$,
      certificateTypeObservable$
    ).subscribe((_) => {
      this.store.dispatch(new OnChangeVariantForm());
    });

    this.licenseForm$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((licenseForm) => licenseForm && !!licenseForm.model),
        map((licenseForm) => licenseForm.model)
      )
      .subscribe(() => this.store.dispatch(new OnChangeBundleVariantForm()));
  }

  subscribeToProductDetails() {
    combineLatest(this.product$, this.selectedVariant$, this.expiringContract$)
      .pipe(
        filter((_) => _ && _.length && !!_[0] && !!_[1]),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([productDetails, selectedVariant, expiringContract]) => {
        if (productDetails) {
          this.videoAboutProduct = productDetails.videos.find(
            (v) => v.featured
          );
          this.isProductBundle =
            productDetails.typeConfig &&
            productDetails.typeConfig.configKey === "bundle-product";
          this.initiateTabs(productDetails, selectedVariant, expiringContract);
          this.store.dispatch(new GetRelatedProducts(productDetails.sku));
        }
      });

    combineLatest([this.includedProductsPagination$, this.product$])
      .pipe(
        filter(
          ([pagination, product]) =>
            !!(product && product.bundle && product.bundle.sku)
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([pagination, product]: [IPageable, Product]) => {
        this.store.dispatch(
          new GetIncludedProducts(product.bundle.sku, pagination)
        );
      });

    combineLatest([this.relatedBundlesPagination$, this.product$])
      .pipe(
        filter(([pagination, product]) => !!product),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([pagination, product]: [IPageable, Product]) => {
        this.store.dispatch(
          new GetProductRelatedBundles(product.sku, pagination)
        );
      });
  }

  initiateTabs(
    productDetails: Product,
    selectedVariant: ProductVariantShort,
    expiringContract: Contract
  ) {
    const tabs = [];
    tabs.push({ value: "about", displayValue: "About" });

    if (
      !this.isProductExam(productDetails) &&
      this.productDetailsService.isCourseInformationAvailable(productDetails)
    ) {
      tabs.push({
        value: "course-information",
        displayValue: "Course Information",
      });
    }

    if (!this.isProductExam(productDetails) && !this.isProductBundle) {
      tabs.push({ value: "content", displayValue: "Content" });
    }

    if (this.isProductBundle) {
      tabs.push({ value: "products", displayValue: "Products" });
    }

    const expiryDate = expiringContract && expiringContract.expiryDate;
    if (
      (productDetails.restriction &&
        productDetails.restriction.restricted &&
        !productDetails.restriction.hasAccess) ||
      (expiryDate &&
        differenceInDays(new Date(expiryDate * 1000), new Date()) <= 28)
    ) {
      tabs.push({ value: "how-to-access", displayValue: "How To Access" });
    }

    tabs.push({
      value: "delivery-and-specifications",
      displayValue: "Specifications",
    });
    tabs.push({ value: "media", displayValue: "Media" });
    tabs.push({ value: "reviews", displayValue: "Reviews" });
    tabs.push({ value: "bundles", displayValue: "Bundles" });

    this.tabs = tabs;
  }

  isTabSelected(selectedTab: string) {
    return this.tabs.findIndex((_) => _.value === selectedTab);
  }

  toggleTab(key: string) {
    this.store.dispatch([new ToggleTab(key)]);
  }

  isProductExam(product: Product) {
    return product.type === "Exam";
  }

  addRelatedProductToCart(sku: string) {
    this.store.dispatch(new AddLineItemToShoppingCart(sku, 1));
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.store.dispatch(new ClearProductDetailsState());
  }

  getAccess(segment: string) {
    this.store.dispatch(new GetAccess(segment));
  }

  updateProductDetails(product: Partial<Product>) {
    this.store.dispatch(new UpdateProductDetails(product));
  }

  includedProductsPageChanged(pagination: IPageable): void {
    this.store.dispatch(new SetIncludedProductsPagination(pagination));
  }

  relatedBundlesPageChanged(pagination: IPageable): void {
    this.store.dispatch(new SetProductRelatedBundlesPagination(pagination));
  }
}
